import { useTranslation } from 'react-i18next';
import './ModuleProgress.scss';
import { useMemo, useState } from 'react';
import { useServices } from 'index';
import { useQuery } from '@tanstack/react-query';
import Loader from '@trackman/web-shared-components/components/Loader/Loader';
import Paragraph from 'components/TextComps/Paragraph';
import { Tab, Tabs } from '@trackman/web-shared-components';
import { Subtitle } from 'components/TextComps/Subtitle';
import { StudentProgress } from '../StudentProgress/StudentProgress';
import { ClassProgress } from '../ClassProgress/ClassProgress';
import NoStudentsFound from 'components/Organization/NoStudentsFound/NoStudentsFound';

type Props = {
  selectedClassId: string;
  selectedModuleId: string;
  selectedModuleName: string;
  isModuleProgressQueryEnabled: boolean;
  selectedProgressOverviewIndex: number;
  studentsFilter: string;
  setSelectedProgressOverviewIndex: (index: number) => void;
};

export const ModuleProgress = ({
  selectedClassId,
  selectedModuleId,
  selectedModuleName,
  isModuleProgressQueryEnabled,
  selectedProgressOverviewIndex,
  studentsFilter,
  setSelectedProgressOverviewIndex,
}: Props) => {
  const { t } = useTranslation();

  const moduleProgressService = useServices().moduleProgressService;

  const { data: moduleProgress, isPending: isModuleProgressPending } = useQuery({
    queryKey: ['moduleProgress', `${selectedClassId}-${selectedModuleId}`],
    queryFn: () => moduleProgressService.getClassProgressForSelectedModule(selectedClassId, selectedModuleId),
    enabled: isModuleProgressQueryEnabled,
    refetchOnWindowFocus: false,
  });

  const filteredModuleProgress = useMemo(() => {
    return moduleProgress?.filter((student) => student.name.toLowerCase().includes(studentsFilter.toLowerCase()));
  }, [moduleProgress, studentsFilter]);

  return (
    <div className='module-progress-table-wrapper'>
      {isModuleProgressPending ? (
        <div className='loader-wrapper'>
          <Loader size='large' />
        </div>
      ) : (
        <>
          <div className='module-progress-table-header'>
            <div className='module-progress-table-title'>
              <span>{t('Module', { ns: 'organizationPage' })}</span>
              <Subtitle>
                <>{selectedModuleName}</>
              </Subtitle>
            </div>
            <div className='module-progress-table-tabs'>
              <Tabs
                value={selectedProgressOverviewIndex}
                onChange={(event, newValue: number) => setSelectedProgressOverviewIndex(newValue)}
              >
                <Tab label={t('StudentProgress', { ns: 'organizationPage' })} />
                <Tab label={t('ClassProgress', { ns: 'organizationPage' })} />
              </Tabs>
            </div>
          </div>

          <>
            {selectedProgressOverviewIndex === 0 && (
              <div role='tabpanel' id='students-progress-tab' aria-labelledby='students-progress-tab'>
                {!filteredModuleProgress?.length ? (
                  <NoStudentsFound />
                ) : (
                  <StudentProgress filteredModuleProgress={filteredModuleProgress} />
                )}
              </div>
            )}
            {selectedProgressOverviewIndex === 1 && (
              <div role='tabpanel' id='class-progress-tab' aria-labelledby='class-progress-tab'>
                {!moduleProgress?.length ? <NoStudentsFound /> : <ClassProgress moduleProgress={moduleProgress} classId={selectedClassId} />}
              </div>
            )}
          </>
        </>
      )}
    </div>
  );
};
